/**
 * Gráfico de barras agrupadas: Ingreso / Gastos / Utilidad por mes,
 * comparando dos años. Construido con SVG puro (sin librería externa)
 * para mantener consistencia con el resto de la Suite.
 *
 * Responsive: se escala al 100% del contenedor.
 */
import { useMemo } from 'react';
import { YearComparisonSummaryDto } from '../../../../../shared/services/results/types.comparativos';
import { money } from './comparativosUtils';

// Reconstruye los datos por mes a partir de las filas de cuenta
function buildChartData(data: YearComparisonSummaryDto) {
  const byMonth1: Record<string, { ingreso: number; gastos: number }> = {};
  const byMonth2: Record<string, { ingreso: number; gastos: number }> = {};

  // No tenemos el desglose por mes en el YearComparisonSummaryDto (solo el total anual por cuenta).
  // El gráfico aquí muestra los KPIs globales como barras simples (los tres KPIs lado a lado).
  // Para el desglose mes a mes se usa el SummaryYearTab (ResultsSummaryTab con comparación).
  return [
    { label: 'Ingreso', v1: data.ingresoAnio1, v2: data.ingresoAnio2 },
    { label: 'Gastos', v1: data.gastosAnio1, v2: data.gastosAnio2 },
    { label: 'Utilidad', v1: data.utilidadAnio1, v2: data.utilidadAnio2 },
  ];
}

const COLORS = {
  anio1: '#94a3b8', // slate-400
  anio2Ingreso: '#10b981', // emerald-500
  anio2Gastos: '#ef4444', // red-500
  anio2Utilidad: '#3b82f6', // blue-500
};

interface Props {
  data: YearComparisonSummaryDto;
  anio1: number;
  anio2: number;
}

export function YearVsYearChart({ data, anio1, anio2 }: Props) {
  const series = buildChartData(data);
  const maxVal = Math.max(...series.flatMap((s) => [Math.abs(s.v1), Math.abs(s.v2)]), 1);

  const W = 600;
  const H = 260;
  const PAD = { top: 20, right: 20, bottom: 50, left: 80 };
  const chartH = H - PAD.top - PAD.bottom;
  const chartW = W - PAD.left - PAD.right;

  const groupW = chartW / series.length;
  const barW = (groupW * 0.35);
  const gap = groupW * 0.05;

  const colors2 = [COLORS.anio2Ingreso, COLORS.anio2Gastos, COLORS.anio2Utilidad];

  const barHeight = (v: number) => Math.max(0, (Math.abs(v) / maxVal) * chartH);

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-ink-700">Comparativo global {anio1} vs {anio2}</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-2xl" aria-label="Comparativo año vs año">
        {/* Líneas de referencia horizontales */}
        {[0.25, 0.5, 0.75, 1].map((t) => {
          const y = PAD.top + chartH - t * chartH;
          return (
            <g key={t}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#e2e8f0" strokeWidth={1} />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">
                {(maxVal * t / 1_000_000).toFixed(0)}M
              </text>
            </g>
          );
        })}

        {/* Barras */}
        {series.map((s, i) => {
          const groupX = PAD.left + i * groupW + groupW / 2;
          const x1 = groupX - barW - gap / 2;
          const x2 = groupX + gap / 2;
          const bh1 = barHeight(s.v1);
          const bh2 = barHeight(s.v2);
          return (
            <g key={s.label}>
              {/* Barra año1 */}
              <rect x={x1} y={PAD.top + chartH - bh1} width={barW} height={bh1} fill={COLORS.anio1} rx={3} opacity={0.85}>
                <title>{s.label} {anio1}: {money(s.v1)}</title>
              </rect>
              {/* Barra año2 */}
              <rect x={x2} y={PAD.top + chartH - bh2} width={barW} height={bh2} fill={colors2[i]} rx={3}>
                <title>{s.label} {anio2}: {money(s.v2)}</title>
              </rect>
              {/* Etiqueta grupo */}
              <text x={groupX} y={PAD.top + chartH + 16} textAnchor="middle" fontSize={11} fontWeight={600} fill="#475569">
                {s.label}
              </text>
            </g>
          );
        })}

        {/* Eje base */}
        <line x1={PAD.left} y1={PAD.top + chartH} x2={W - PAD.right} y2={PAD.top + chartH} stroke="#cbd5e1" strokeWidth={1.5} />

        {/* Leyenda */}
        <rect x={PAD.left} y={H - 18} width={10} height={10} fill={COLORS.anio1} rx={2} />
        <text x={PAD.left + 14} y={H - 9} fontSize={10} fill="#64748b">{anio1}</text>
        <rect x={PAD.left + 60} y={H - 18} width={10} height={10} fill={COLORS.anio2Ingreso} rx={2} />
        <text x={PAD.left + 74} y={H - 9} fontSize={10} fill="#64748b">{anio2} Ingreso</text>
        <rect x={PAD.left + 150} y={H - 18} width={10} height={10} fill={COLORS.anio2Gastos} rx={2} />
        <text x={PAD.left + 164} y={H - 9} fontSize={10} fill="#64748b">{anio2} Gastos</text>
        <rect x={PAD.left + 230} y={H - 18} width={10} height={10} fill={COLORS.anio2Utilidad} rx={2} />
        <text x={PAD.left + 244} y={H - 9} fontSize={10} fill="#64748b">{anio2} Utilidad</text>
      </svg>
    </div>
  );
}
